import { type FormEvent, useEffect, useState } from "react";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import { KeyRound, Lock, Mail, UserPlus } from "lucide-react";
import { useAuthSession } from "@/hooks/useAuthSession";

const Login = () => {
  usePageSeo({ path: "/login", title: "Sign In | Salesby", description: "Sign in to your Salesby sales CRM account.", noindex: true });

  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { session, isReady, acceptSession, refreshSession } = useAuthSession();
  const from = (location.state as { from?: string } | null)?.from;
  const destination = from && from !== "/login" ? from : "/index";

  useEffect(() => {
    if (isReady && session) {
      navigate(destination, { replace: true });
    }
  }, [destination, isReady, navigate, session]);

  const handleLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (error) {
      setLoading(false);
      toast({ title: "Login Failed", description: error.message, variant: "destructive" });
      return;
    }

    if (data.session) {
      acceptSession(data.session);
    } else {
      await refreshSession();
    }

    setLoading(false);
    navigate(destination, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto">
          <div className="flex justify-center mb-6">
            <img src={"/logo-final.png"} alt="Salesby Logo" className="h-20 w-auto object-contain" />
          </div>
          <Card>
            <CardContent className="space-y-4 pt-6">
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="pl-10"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    disabled={loading}
                    required
                  />
                </div>

                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="pl-10"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    disabled={loading}
                    required
                  />
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
              </form>
            </CardContent>
            <CardFooter className="flex justify-center gap-6 text-sm">
              <Link to="/reset-password" className="flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors">
                <KeyRound className="h-4 w-4" />
                <span>Reset</span>
              </Link>
              <Link to="/register" className="flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors">
                <UserPlus className="h-4 w-4" />
                <span>Sign Up</span>
              </Link>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
